import React, { useState, useEffect, useCallback } from 'react';
import LibraryService from '../services/LibraryService';
import { Check, ChevronLeft, ChevronRight, BookOpen, Lock } from 'lucide-react';

const MASCOT_URL = 'https://customer-assets.emergentagent.com/job_7b59aa73-04f7-469e-b3b0-3b5db031400b/artifacts/0zxv9crx_02D61451-5FF5-40EF-9C50-17F42BA59024.png';
const MASCOT_FULL = 'https://customer-assets.emergentagent.com/job_dialogue-digest-1/artifacts/17m8qo3i_image.png';

// ─── Child Picker (mascotte grande dépassant le cadre + boutons blancs pill) ─
const ChildPickerPopup = ({ children, onSelect, onClose }) => (
  <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" style={{ background: 'rgba(0,0,0,0.25)' }}>
    <div className="relative w-full sm:max-w-sm" data-testid="library-child-picker">
      {/* Mascotte grande qui dépasse au-dessus du cadre */}
      <div className="flex justify-center relative z-10" style={{ marginBottom: '-30px' }}>
        <img
          src={MASCOT_FULL}
          alt="Mascotte"
          className="object-contain"
          style={{
            width: '180px',
            height: '180px',
            filter: 'drop-shadow(0 8px 20px rgba(0,0,0,0.15))',
          }}
        />
      </div>

      {/* Cadre blanc semi-transparent */}
      <div
        className="rounded-t-3xl sm:rounded-3xl shadow-2xl px-6 pb-6 pt-10"
        style={{ background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(16px)' }}
      >
        <p
          className="text-center text-[#333] font-bold mb-5"
          style={{ fontFamily: '"Caveat", cursive', fontSize: '1.5rem' }}
        >
          Pour quel enfant ?
        </p>

        <div className="grid grid-cols-2 gap-3">
          {children.map((child) => (
            <button
              key={child.id}
              onClick={() => onSelect(child)}
              data-testid={`child-pick-${child.id}`}
              className="py-5 rounded-[2rem] bg-white border border-gray-200 transition-all hover:shadow-lg active:scale-95"
              style={{
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              }}
            >
              <span
                className="text-[#333] block text-center"
                style={{ fontFamily: '"Caveat", cursive', fontSize: '1.6rem' }}
              >
                {child.name}
              </span>
            </button>
          ))}
        </div>
        <button onClick={onClose} className="w-full mt-4 py-2 text-sm text-gray-400 hover:text-gray-600">Annuler</button>
      </div>
    </div>
  </div>
);

// ─── Quiz Bravo Animation (auto-close comme newsletter) ──────────────────────
const LibraryBravoScreen = ({ onComplete, score, total }) => {
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setFadeOut(true), 2000);
    const t2 = setTimeout(onComplete, 2500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onComplete]);

  return (
    <div
      className={`fixed inset-0 z-[60] flex items-center justify-center transition-opacity duration-500 ${fadeOut ? 'opacity-0' : 'opacity-100'}`}
      style={{ background: 'linear-gradient(135deg, #4CAF50 0%, #45B7AA 50%, #4A90D9 100%)' }}
    >
      <div className="text-center text-white">
        <div className="text-6xl mb-4">&#128079;</div>
        <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: "'Caveat', cursive" }}>Bravo !</h2>
        <p className="text-lg opacity-90">{score}/{total} bonnes r&eacute;ponses</p>
      </div>
    </div>
  );
};

// ─── Animation "Génial !" (identique QuizPage) ──────────────────────────────
const SuccessAnimation = ({ onComplete }) => {
  useEffect(() => {
    const t = setTimeout(onComplete, 1800);
    return () => clearTimeout(t);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
      <div className="text-center animate-bounce">
        <div className="text-5xl mb-2">&#128077;</div>
        <div className="text-3xl font-bold text-[#4CAF50]" style={{ fontFamily: "'Caveat', cursive" }}>
          G&eacute;nial !
        </div>
      </div>
      {[...Array(3)].map((_, i) => (
        <div
          key={i}
          className="absolute text-2xl animate-ping"
          style={{ left: `${30 + i * 20}%`, top: '40%', animationDuration: `${0.5 + i * 0.1}s`, animationDelay: `${i * 0.15}s` }}
        >
          &#128077;
        </div>
      ))}
    </div>
  );
};

// ─── Library Quiz Flow ───────────────────────────────────────────────────────
const LibraryQuiz = ({ questions, questionId, childId, onComplete, api }) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [attempts, setAttempts] = useState(0); // 0, 1, or 2
  const [showCorrect, setShowCorrect] = useState(false); // after 2nd wrong answer
  const [showGenial, setShowGenial] = useState(false);
  const [score, setScore] = useState(0);
  const [shakeIdx, setShakeIdx] = useState(null); // index of option to shake
  const [showBravo, setShowBravo] = useState(false);
  const [canHarvest, setCanHarvest] = useState(false);
  const [showHarvest, setShowHarvest] = useState(false);
  const [locked, setLocked] = useState(false); // prevent clicks during animations

  const q = questions[currentIdx];

  const advanceToNext = () => {
    if (currentIdx < questions.length - 1) {
      setCurrentIdx(i => i + 1);
      setSelected(null);
      setAttempts(0);
      setShowCorrect(false);
      setShakeIdx(null);
      setLocked(false);
    } else {
      completeQuiz();
    }
  };

  const handleAnswer = (idx) => {
    if (locked) return;

    const correct = idx === q.correct_index;

    if (correct) {
      setSelected(idx);
      setScore(s => s + 1);
      setLocked(true);
      setShowGenial(true);
    } else {
      // Wrong answer
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setSelected(idx);
      setShakeIdx(idx);

      if (newAttempts >= 2) {
        // 2nd wrong answer → show correct in green, auto-advance
        setLocked(true);
        setShowCorrect(true);
        setTimeout(() => {
          advanceToNext();
        }, 2000);
      } else {
        // 1st wrong answer → shake, then allow retry
        setTimeout(() => {
          setShakeIdx(null);
          setSelected(null);
        }, 800);
      }
    }
  };

  const handleGenialComplete = () => {
    setShowGenial(false);
    advanceToNext();
  };

  const completeQuiz = async () => {
    try {
      const svc = LibraryService.getInstance();
      svc.setApi(api);
      const res = await svc.completeQuiz(questionId, childId);
      setCanHarvest(res.can_harvest_star);
    } catch (e) {
      console.error('complete quiz error:', e);
    }
    setShowBravo(true);
  };

  const handleBravoComplete = () => {
    setShowBravo(false);
    if (canHarvest) {
      setShowHarvest(true);
    } else {
      onComplete(true);
    }
  };

  const handleHarvest = async () => {
    try {
      const svc = LibraryService.getInstance();
      svc.setApi(api);
      const res = await svc.harvestStar();
      if (res.earned) {
        window.dispatchEvent(new CustomEvent('stars-earned', { detail: { amount: 1, newBalance: res.new_balance } }));
      }
    } catch (e) {
      console.error('harvest error:', e);
    }
    setShowHarvest(false);
    onComplete(true);
  };

  if (showBravo) {
    return <LibraryBravoScreen score={score} total={questions.length} onComplete={handleBravoComplete} />;
  }

  if (showHarvest) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }}>
        <div className="bg-white rounded-3xl p-6 max-w-sm w-full mx-4 text-center shadow-2xl">
          <div className="text-5xl mb-3">&#11088;</div>
          <h3 className="text-xl font-bold text-amber-700 mb-2" style={{ fontFamily: "'Caveat', cursive" }}>
            5 questions compl&eacute;t&eacute;es !
          </h3>
          <p className="text-gray-600 text-sm mb-4">Tu as gagn&eacute; une &eacute;toile bonus !</p>
          <button
            onClick={handleHarvest}
            data-testid="library-harvest-star-btn"
            className="w-full py-3 rounded-full font-bold text-white transition-all hover:opacity-90 active:scale-[0.98]"
            style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' }}
          >
            R&eacute;colter l'&eacute;toile &#11088;
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {showGenial && <SuccessAnimation onComplete={handleGenialComplete} />}

      <div className="flex items-center justify-center gap-2 mb-2">
        <span className="text-sm font-bold text-gray-700">Question {currentIdx + 1}/{questions.length}</span>
        <div className="flex gap-1.5">
          {questions.map((_, i) => (
            <div key={i} className={`w-3 h-3 rounded-full transition-colors ${i <= currentIdx ? 'bg-[#5BBFBD]' : 'bg-gray-200'}`} />
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl p-5 shadow-md" data-testid="library-quiz-question">
        <h3 className="text-lg font-bold text-gray-800 text-center leading-snug">{q.question}</h3>
      </div>

      <div className="space-y-2">
        {(q.options || []).map((option, idx) => {
          let classes = 'bg-white text-gray-800 border border-transparent';
          let animStyle = {};

          // Correct answer selected → green
          if (selected === idx && idx === q.correct_index) {
            classes = 'bg-green-100 text-green-800 border-2 border-green-400';
          }
          // 2nd wrong → show correct answer in green
          else if (showCorrect && idx === q.correct_index) {
            classes = 'bg-green-100 text-green-800 border-2 border-green-400';
          }
          // Wrong answer selected → red + shake
          else if (selected === idx && idx !== q.correct_index && (shakeIdx === idx || showCorrect)) {
            classes = 'bg-red-100 text-red-700 border-2 border-red-300';
            if (shakeIdx === idx) {
              animStyle = { animation: 'shakeOption 0.4s ease-in-out' };
            }
          }

          return (
            <button
              key={idx}
              onClick={() => handleAnswer(idx)}
              disabled={locked}
              className={`w-full text-left p-4 rounded-xl shadow-sm transition-all text-sm font-medium ${classes}`}
              style={animStyle}
              data-testid={`library-quiz-option-${idx}`}
            >
              {option}
            </button>
          );
        })}
      </div>

      <style>{`
        @keyframes shakeOption {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-5px); }
          80% { transform: translateX(5px); }
        }
      `}</style>
    </div>
  );
};

// ─── Question Detail View ────────────────────────────────────────────────────
const QuestionDetail = ({ questionId, childId, children, onBack, api }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState(null);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [showChildPicker, setShowChildPicker] = useState(false);
  const [selectedChildId, setSelectedChildId] = useState(childId);

  const loadQuestion = useCallback(async (cid) => {
    setLoading(true);
    try {
      const svc = LibraryService.getInstance();
      svc.setApi(api);
      const res = await svc.getQuestion(questionId, cid);
      setData(res);
    } catch (e) {
      console.error('Load question error:', e);
    }
    setLoading(false);
  }, [questionId, api]);

  useEffect(() => {
    if (children.length > 1 && !selectedChildId) {
      setShowChildPicker(true);
    } else {
      const cid = selectedChildId || (children.length === 1 ? children[0].id : '');
      setSelectedChildId(cid);
      loadQuestion(cid);
    }
  }, []);

  const handleChildSelect = (child) => {
    setSelectedChildId(child.id);
    setShowChildPicker(false);
    loadQuestion(child.id);
  };

  const startQuiz = async () => {
    try {
      const svc = LibraryService.getInstance();
      svc.setApi(api);
      const res = await svc.getQuiz(questionId);
      if (res && res.questions && res.questions.length > 0) {
        setQuizQuestions(res.questions);
        setShowQuiz(true);
      }
    } catch (e) {
      console.error('Load quiz error:', e);
    }
  };

  const handleQuizComplete = () => {
    // Après Bravo auto-close → retour direct à la liste des questions du thème
    onBack();
  };

  if (showChildPicker) {
    return <ChildPickerPopup children={children} onSelect={handleChildSelect} onClose={onBack} />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#5BBFBD]"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-500">Cette question n'est pas encore disponible.</p>
        <button onClick={onBack} className="mt-4 text-[#4A90A4] font-medium">Retour</button>
      </div>
    );
  }

  if (showQuiz && quizQuestions) {
    return (
      <div className="px-1">
        <button onClick={() => setShowQuiz(false)} className="flex items-center gap-1 text-[#4A90A4] text-sm font-medium mb-4">
          <ChevronLeft className="w-4 h-4" /> Retour
        </button>
        <LibraryQuiz
          questions={quizQuestions}
          questionId={questionId}
          childId={selectedChildId}
          onComplete={handleQuizComplete}
          api={api}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 px-1 overflow-y-auto pb-6" style={{ maxHeight: 'calc(100vh - 280px)', WebkitOverflowScrolling: 'touch' }}>
      <button onClick={onBack} className="flex items-center gap-1 text-[#4A90A4] text-sm font-medium sticky top-0 bg-transparent z-10 py-1" data-testid="question-back-btn">
        <ChevronLeft className="w-4 h-4" /> Retour
      </button>

      {/* Question bubble — GAUCHE (enfant qui pose la question) */}
      <div className="flex justify-start">
        <div
          className="max-w-[80%] px-4 py-3 rounded-2xl shadow-md text-white"
          style={{ background: 'linear-gradient(135deg, #4FBDBA 0%, #5BBFBD 100%)' }}
          data-testid="library-question-bubble"
        >
          <p className="text-base leading-relaxed">{data.question}</p>
        </div>
      </div>

      {/* Answer bubble — DROITE, large (Dis Maman qui répond) */}
      <div className="flex justify-end">
        <div className="w-[95%] px-4 py-3 rounded-2xl shadow-md bg-white text-[#333]" data-testid="library-answer-bubble">
          <p className="text-base leading-relaxed whitespace-pre-wrap">{data.answer}</p>
        </div>
      </div>

      {/* Quiz button */}
      {data.has_quiz && !quizCompleted && (
        <div className="flex justify-center pt-2">
          <button
            onClick={startQuiz}
            data-testid="library-start-quiz-btn"
            className="flex items-center gap-2 px-6 py-3 rounded-full text-white font-semibold shadow-md hover:opacity-90 transition-all active:scale-95"
            style={{ background: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)' }}
          >
            <span className="text-lg">&#10067;</span>
            <span>Lancer le quiz</span>
          </button>
        </div>
      )}

      {quizCompleted && (
        <div className="text-center py-2">
          <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-4 py-2">
            <Check className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-700">Quiz termin&eacute; !</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Premium Upsell Modal ────────────────────────────────────────────────────
const PremiumUpsellModal = ({ onClose }) => (
  <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" style={{ background: 'rgba(0,0,0,0.35)' }}>
    <div
      className="w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl shadow-2xl px-6 pb-6 pt-8 text-center"
      style={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(16px)' }}
      data-testid="premium-upsell-modal"
    >
      <Lock className="w-10 h-10 text-amber-500 mx-auto mb-3" />
      <h3
        className="text-2xl font-bold text-[#333] mb-2"
        style={{ fontFamily: "'Caveat', cursive" }}
      >
        Contenu r&eacute;serv&eacute; aux abonn&eacute;s
      </h3>
      <p className="text-sm text-gray-600 mb-5 leading-relaxed">
        Abonne-toi pour d&eacute;bloquer les 100 questions de la biblioth&egrave;que, les quiz et gagner des &eacute;toiles&nbsp;!
      </p>
      <button
        onClick={() => { window.location.href = '/subscription'; }}
        data-testid="upsell-subscribe-btn"
        className="w-full py-3 rounded-full font-bold text-white transition-all hover:opacity-90 active:scale-[0.98] mb-3"
        style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' }}
      >
        D&eacute;couvrir les offres
      </button>
      <button
        onClick={onClose}
        data-testid="upsell-close-btn"
        className="w-full py-2 text-sm text-gray-400 hover:text-gray-600"
      >
        Plus tard
      </button>
    </div>
  </div>
);

// ─── Theme Questions List ────────────────────────────────────────────────────
const ThemeQuestionsList = ({ themeId, childId, children, onBack, api }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showUpsell, setShowUpsell] = useState(false);

  const loadQuestions = useCallback(async () => {
    setLoading(true);
    const svc = LibraryService.getInstance();
    svc.setApi(api);
    const res = await svc.getThemeQuestions(themeId, childId);
    setData(res);
    setLoading(false);
  }, [themeId, childId, api]);

  useEffect(() => { loadQuestions(); }, [loadQuestions]);

  if (selectedQuestion) {
    return (
      <QuestionDetail
        questionId={selectedQuestion}
        childId={childId}
        children={children}
        onBack={() => { setSelectedQuestion(null); loadQuestions(); }}
        api={api}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#5BBFBD]"></div>
      </div>
    );
  }

  const questions = data?.questions || [];
  const completedCount = questions.filter(q => q.completed).length;

  return (
    <div className="space-y-3 px-1">
      {showUpsell && <PremiumUpsellModal onClose={() => setShowUpsell(false)} />}

      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1 text-[#4A90A4] text-sm font-medium" data-testid="theme-back-btn">
          <ChevronLeft className="w-4 h-4" /> Retour
        </button>
        <span className="text-xs font-medium text-gray-500 bg-white/70 px-3 py-1 rounded-full">
          {completedCount}/{questions.length}
        </span>
      </div>

      <h2 className="text-xl font-bold text-[#333]" style={{ fontFamily: "'Caveat', cursive" }}>
        {data?.theme_icon} {data?.theme_name}
      </h2>

      <div className="space-y-2">
        {questions.map((q) => {
          const isLocked = q.locked;

          return (
            <button
              key={q.id}
              onClick={() => {
                if (isLocked) { setShowUpsell(true); return; }
                if (q.generated) setSelectedQuestion(q.id);
              }}
              data-testid={`library-q-${q.id}`}
              className={`w-full text-left p-4 rounded-xl shadow-sm transition-all flex items-center gap-3 ${
                isLocked
                  ? 'bg-gray-50 cursor-pointer'
                  : q.completed
                  ? 'bg-green-50 border border-green-200'
                  : q.generated
                  ? 'bg-white hover:bg-gray-50 active:scale-[0.99]'
                  : 'bg-gray-100 opacity-50 cursor-not-allowed'
              }`}
              style={isLocked ? { opacity: 0.45, filter: 'grayscale(0.6)' } : {}}
            >
              {isLocked ? (
                <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-3.5 h-3.5 text-gray-500" />
                </div>
              ) : q.completed ? (
                <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-white" />
                </div>
              ) : (
                <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex-shrink-0" />
              )}
              <span className={`text-sm font-medium flex-1 ${
                isLocked ? 'text-gray-400' : q.completed ? 'text-green-700' : 'text-gray-800'
              }`}>
                {q.question}
              </span>
              {isLocked ? (
                <Lock className="w-4 h-4 text-gray-300 flex-shrink-0" />
              ) : q.generated ? (
                <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Themes List (main library view) ─────────────────────────────────────────
const LibraryView = ({ childId, children, api }) => {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [showUpsell, setShowUpsell] = useState(false);

  const loadThemes = useCallback(async () => {
    setLoading(true);
    const svc = LibraryService.getInstance();
    svc.setApi(api);
    const data = await svc.getThemes(childId);
    setThemes(data);
    setLoading(false);
  }, [childId, api]);

  useEffect(() => { loadThemes(); }, [loadThemes]);

  if (selectedTheme) {
    return (
      <ThemeQuestionsList
        themeId={selectedTheme}
        childId={childId}
        children={children}
        onBack={() => { setSelectedTheme(null); loadThemes(); }}
        api={api}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#5BBFBD]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 px-1">
      {showUpsell && <PremiumUpsellModal onClose={() => setShowUpsell(false)} />}

      <div className="flex items-center gap-2 mb-2">
        <BookOpen className="w-5 h-5 text-[#4A90A4]" />
        <h2 className="text-xl font-bold text-[#333]" style={{ fontFamily: "'Caveat', cursive" }}>
          Biblioth&egrave;que de questions
        </h2>
      </div>

      <div className="space-y-2">
        {themes.map((theme) => {
          const isLocked = theme.locked;

          return (
            <button
              key={theme.id}
              onClick={() => {
                if (isLocked) { setShowUpsell(true); return; }
                setSelectedTheme(theme.id);
              }}
              data-testid={`library-theme-${theme.id}`}
              className={`w-full text-left p-4 rounded-xl shadow-sm transition-all flex items-center gap-3 ${
                isLocked
                  ? 'bg-white/60 cursor-pointer'
                  : 'bg-white hover:shadow-md active:scale-[0.99]'
              }`}
              style={isLocked ? { opacity: 0.45, filter: 'grayscale(0.6)' } : {}}
            >
              <span className="text-2xl flex-shrink-0">{theme.icon}</span>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold truncate ${isLocked ? 'text-gray-400' : 'text-gray-800'}`}>{theme.name}</p>
                <p className="text-xs text-gray-500">{theme.total} questions</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {!isLocked && theme.completed > 0 && (
                  <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                    {theme.completed}/{theme.total}
                  </span>
                )}
                {isLocked ? (
                  <Lock className="w-4 h-4 text-gray-300" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default LibraryView;
